import { CheckCircle, AlertCircle, XCircle } from 'lucide-react';

interface AlertModalProps {
  isOpen: boolean;
  type?: 'info' | 'success' | 'warning' | 'error';
  title?: string;
  message: string;
  buttonText?: string;
  onClose: () => void;
  isDark: boolean;
}

export default function AlertModal({
  isOpen,
  type = 'info',
  title,
  message,
  buttonText = 'OK',
  onClose,
  isDark,
}: AlertModalProps) {
  if (!isOpen) return null;

  const icons = {
    info: AlertCircle,
    success: CheckCircle,
    warning: AlertCircle,
    error: XCircle,
  };

  const colors = {
    info: { bg: 'bg-cyan-500/10', icon: 'text-cyan-400', button: 'bg-cyan-500 hover:bg-cyan-600' },
    success: { bg: 'bg-emerald-500/10', icon: 'text-emerald-400', button: 'bg-emerald-500 hover:bg-emerald-600' },
    warning: { bg: 'bg-amber-500/10', icon: 'text-amber-400', button: 'bg-amber-500 hover:bg-amber-600' },
    error: { bg: 'bg-red-500/10', icon: 'text-red-400', button: 'bg-red-500 hover:bg-red-600' },
  };

  const Icon = icons[type];
  const color = colors[type];

  const theme = {
    overlay: isDark ? 'bg-black/80' : 'bg-black/50',
    bg: isDark ? 'bg-[#1a1f2e]' : 'bg-white',
    border: isDark ? 'border-white/10' : 'border-gray-200',
    text: isDark ? 'text-white' : 'text-gray-900',
    textMuted: isDark ? 'text-white/70' : 'text-gray-600',
  };

  return (
    <div className={`fixed inset-0 ${theme.overlay} backdrop-blur-sm flex items-center justify-center p-6 z-[60]`}>
      <div className={`${theme.bg} rounded-2xl border ${theme.border} shadow-2xl max-w-md w-full p-6`}>
        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${color.bg}`}>
            <Icon className={`w-8 h-8 ${color.icon}`} />
          </div>
        </div>

        {/* Title */}
        {title && (
          <h3 className={`text-lg font-semibold text-center mb-2 ${theme.text}`}>
            {title}
          </h3>
        )}

        {/* Message */}
        <p className={`text-center mb-6 ${theme.textMuted}`}>
          {message}
        </p>

        {/* Button */}
        <button
          onClick={onClose}
          className={`w-full px-4 py-2.5 rounded-lg font-medium text-white transition-colors ${color.button}`}
        >
          {buttonText}
        </button>
      </div>
    </div>
  );
}

